import { Path } from "../types/type-system";
import { Expr } from "./ast";

/**
 * Every context path an expression reads. Lambda variables are mapped back
 * onto the list they iterate, so `any(trigger.lineItems, li => li.sku = "x")`
 * reads trigger.lineItems.sku. This is what makes a node's data needs (its
 * GraphQL selection) derivable without running it.
 */
export function collectReferences(expr: Expr): Path[] {
  const out: Path[] = [];
  visit(expr, new Map(), out);
  return out;
}

function visit(
  expr: Expr,
  bindings: ReadonlyMap<string, Path | undefined>,
  out: Path[],
): void {
  switch (expr.kind) {
    case "literal":
      return;
    case "ref":
      out.push(expr.path);
      return;
    case "var": {
      const base = bindings.get(expr.name);
      if (base) {
        out.push([...base, ...expr.path]);
      }
      return;
    }
    case "lambda":
      visit(expr.body, bindings, out);
      return;
    case "call": {
      const [first, ...rest] = expr.args;
      const listPath = first ? sourcePath(first, bindings) : undefined;
      if (first) {
        visit(first, bindings, out);
      }
      for (const arg of rest) {
        if (arg.kind === "lambda") {
          const next = new Map(bindings);
          next.set(arg.param, listPath);
          visit(arg.body, next, out);
        } else {
          visit(arg, bindings, out);
        }
      }
      return;
    }
  }
}

function sourcePath(
  expr: Expr,
  bindings: ReadonlyMap<string, Path | undefined>,
): Path | undefined {
  if (expr.kind === "ref") {
    return expr.path;
  }
  if (expr.kind === "var") {
    const base = bindings.get(expr.name);
    return base ? [...base, ...expr.path] : undefined;
  }
  if (expr.kind === "call" && expr.fn === "filter" && expr.args[0]) {
    return sourcePath(expr.args[0], bindings);
  }
  return undefined;
}

const GUARDS = new Set(["isSet", "isNull", "isEmpty"]);

/**
 * References that are explicitly handled for absence (Option-style): the
 * non-final arguments of coalesce, and arguments of isSet/isNull/isEmpty.
 * "May not have run" warnings skip these.
 */
export function guardedReferences(expr: Expr): Path[] {
  const out: Path[] = [];
  walkGuards(expr, out);
  return out;
}

function walkGuards(expr: Expr, out: Path[]): void {
  if (expr.kind === "lambda") {
    walkGuards(expr.body, out);
    return;
  }
  if (expr.kind !== "call") {
    return;
  }
  const guarded =
    expr.fn === "coalesce"
      ? expr.args.slice(0, -1)
      : GUARDS.has(expr.fn)
        ? expr.args
        : [];
  for (const arg of guarded) {
    out.push(...collectReferences(arg));
  }
  for (const arg of expr.args) {
    walkGuards(arg, out);
  }
}
